import { Application } from '../models/application.model';
import { Capability } from '../models/capability.model';
import { TmsSystem } from '../models/tms-system.model';
import { Partner } from '../models/partner.model';
import { Customer } from '../models/customer.model';
import { ApiClient } from '../models/api-client.model';
import { FieldMappingTemplate, TemplateVersionResponse } from '../models/template.model';
import { FieldMapping } from '../models/field-mapping.model';
import { LookupTable } from '../models/lookup-table.model';
import { TransformationLogSummary, TransformationLogDetail } from '../models/transformation-log.model';
import { Deployment } from '../models/deployment.model';
import { Version } from '../models/version.model';

const now = new Date();
const daysAgo = (n: number) => new Date(now.getTime() - n * 86400000);
const isoDaysAgo = (n: number) => daysAgo(n).toISOString();

export const mockApplications: Application[] = [
  {
    id: 'app-workflowai',
    name: 'workflowai',
    displayName: 'WorkflowAI',
    description: 'Primary workflow orchestration product',
    isActive: true,
    createdAt: isoDaysAgo(365),
  },
  {
    id: 'app-mobile',
    name: 'mobile',
    displayName: 'Mobile',
    description: 'Driver and dispatcher mobile experience',
    isActive: true,
    createdAt: isoDaysAgo(300),
  },
  {
    id: 'app-user-workflow',
    name: 'user-workflow',
    displayName: 'User Workflow',
    description: 'User-driven workflow automation',
    isActive: true,
    createdAt: isoDaysAgo(240),
  },
  {
    id: 'app-ltl-navigator',
    name: 'ltl-navigator',
    displayName: 'LTL Navigator',
    description: 'LTL navigation and routing product',
    isActive: true,
    createdAt: isoDaysAgo(200),
  },
];

export const mockCapabilities: Capability[] = [
  // WorkflowAI
  {
    id: 'cap-wfai-import-docs',
    applicationId: 'app-workflowai',
    name: 'import-loads',
    displayName: 'Import Loads',
    description: 'Pull loads from a customer TMS into WorkflowAI',
    direction: 'Inbound',
    isActive: true,
    createdAt: isoDaysAgo(360),
  },
  {
    id: 'cap-wfai-export-docs',
    applicationId: 'app-workflowai',
    name: 'export-documents',
    displayName: 'Export Documents',
    description: 'Push documents from WorkflowAI to the customer system',
    direction: 'Outbound',
    isActive: true,
    createdAt: isoDaysAgo(340),
  },
  {
    id: 'cap-wfai-webhooks',
    applicationId: 'app-workflowai',
    name: 'webhooks',
    displayName: 'Webhooks',
    description: 'Receive event webhooks from a customer system',
    direction: 'Inbound',
    isActive: true,
    createdAt: isoDaysAgo(320),
  },
  // Mobile
  {
    id: 'cap-mobile-import-loads',
    applicationId: 'app-mobile',
    name: 'import-loads',
    displayName: 'Import Loads',
    description: 'Pull load assignments from TMS into the Mobile app',
    direction: 'Inbound',
    isActive: true,
    createdAt: isoDaysAgo(280),
  },
  {
    id: 'cap-mobile-export-scans',
    applicationId: 'app-mobile',
    name: 'export-scans',
    displayName: 'Export Scans',
    description: 'Push driver scan events upstream to the TMS',
    direction: 'Outbound',
    isActive: true,
    createdAt: isoDaysAgo(260),
  },
  // User Workflow
  {
    id: 'cap-uw-import-loads',
    applicationId: 'app-user-workflow',
    name: 'import-loads',
    displayName: 'Import Loads',
    description: 'Pull load data into User Workflow automation',
    direction: 'Inbound',
    isActive: true,
    createdAt: isoDaysAgo(220),
  },
  // LTL Navigator
  {
    id: 'cap-ltl-import-orders',
    applicationId: 'app-ltl-navigator',
    name: 'import-orders',
    displayName: 'Import Orders',
    description: 'Pull orders into LTL Navigator',
    direction: 'Inbound',
    isActive: true,
    createdAt: isoDaysAgo(200),
  },
  {
    id: 'cap-ltl-import-carriers',
    applicationId: 'app-ltl-navigator',
    name: 'import-carriers',
    displayName: 'Import Carriers',
    description: 'Pull carrier data into LTL Navigator',
    direction: 'Inbound',
    isActive: true,
    createdAt: isoDaysAgo(195),
  },
  {
    id: 'cap-ltl-import-shippers',
    applicationId: 'app-ltl-navigator',
    name: 'import-shippers',
    displayName: 'Import Shippers',
    description: 'Pull shipper data into LTL Navigator',
    direction: 'Inbound',
    isActive: true,
    createdAt: isoDaysAgo(190),
  },
  {
    id: 'cap-ltl-export-docs',
    applicationId: 'app-ltl-navigator',
    name: 'export-documents',
    displayName: 'Export Documents',
    description: 'Push documents from LTL Navigator to the customer system',
    direction: 'Outbound',
    isActive: true,
    createdAt: isoDaysAgo(185),
  },
];

export const mockTmsSystems: TmsSystem[] = [
  {
    id: 'conn-mobile-loads-mcleod-v22',
    name: 'mobile-loads-mcleod-v22',
    displayName: 'McLeod v22 — Mobile Import Loads',
    description: 'McLeod LoadMaster v22 connector purposed for the Mobile Import Loads capability',
    version: '22',
    isActive: true,
    applicationId: 'app-mobile',
    capabilityId: 'cap-mobile-import-loads',
    sampleJsonSchema: '{"orderId":"string","origin":"string","destination":"string"}',
    createdAt: daysAgo(180),
    updatedAt: daysAgo(40),
    createdBy: 'devs',
  },
  {
    id: 'conn-mobile-loads-mcleod-v23',
    name: 'mobile-loads-mcleod-v23',
    displayName: 'McLeod v23 — Mobile Import Loads',
    description: 'McLeod LoadMaster v23 connector purposed for the Mobile Import Loads capability',
    version: '23',
    isActive: true,
    applicationId: 'app-mobile',
    capabilityId: 'cap-mobile-import-loads',
    sampleJsonSchema: '{"orderId":"string","origin":"string","destination":"string"}',
    createdAt: daysAgo(90),
    updatedAt: daysAgo(5),
    createdBy: 'devs',
  },
  {
    id: 'conn-mobile-scans-sftp',
    name: 'mobile-scans-sftp',
    displayName: 'SFTP — Mobile Export Scans',
    description: 'SFTP file drop connector for pushing driver scan events from the Mobile app',
    version: '1.0',
    isActive: true,
    applicationId: 'app-mobile',
    capabilityId: 'cap-mobile-export-scans',
    sampleJsonSchema: '{"scanId":"string","timestamp":"string","loadId":"string"}',
    createdAt: daysAgo(30),
    updatedAt: daysAgo(1),
    createdBy: 'devs',
  },
  {
    id: 'conn-wfai-import-docs-mcleod',
    name: 'wfai-import-docs-mcleod',
    displayName: 'McLeod v22 — WorkflowAI Import Loads',
    description: 'McLeod LoadMaster v22 connector purposed for WorkflowAI load import',
    version: '22',
    isActive: true,
    applicationId: 'app-workflowai',
    capabilityId: 'cap-wfai-import-docs',
    sampleJsonSchema: '{"docId":"string","type":"string","payload":"string"}',
    createdAt: daysAgo(170),
    updatedAt: daysAgo(20),
    createdBy: 'devs',
  },
  {
    id: 'conn-wfai-import-docs-sap',
    name: 'wfai-import-docs-sap',
    displayName: 'SAP S/4 HANA — WorkflowAI Import Loads',
    description: 'SAP S/4 HANA connector purposed for WorkflowAI load import',
    version: '2023',
    isActive: true,
    applicationId: 'app-workflowai',
    capabilityId: 'cap-wfai-import-docs',
    sampleJsonSchema: '{"docId":"string","category":"string","payload":"string"}',
    createdAt: daysAgo(120),
    updatedAt: daysAgo(12),
    createdBy: 'devs',
  },
  {
    id: 'conn-wfai-export-docs-sap',
    name: 'wfai-export-docs-sap',
    displayName: 'SAP S/4 HANA — WorkflowAI Export Documents',
    description: 'SAP S/4 HANA connector purposed for WorkflowAI document export',
    version: '2023',
    isActive: true,
    applicationId: 'app-workflowai',
    capabilityId: 'cap-wfai-export-docs',
    sampleJsonSchema: '{"docId":"string","status":"string"}',
    createdAt: daysAgo(115),
    updatedAt: daysAgo(11),
    createdBy: 'devs',
  },
  {
    id: 'conn-wfai-webhooks-receiver',
    name: 'wfai-webhooks-receiver',
    displayName: 'Webhook Receiver — WorkflowAI Webhooks',
    description: 'Generic inbound webhook receiver purposed for WorkflowAI event webhooks',
    version: '1.0',
    isActive: true,
    applicationId: 'app-workflowai',
    capabilityId: 'cap-wfai-webhooks',
    sampleJsonSchema: '{"event":"string","timestamp":"string","payload":"object"}',
    createdAt: daysAgo(45),
    updatedAt: daysAgo(2),
    createdBy: 'devs',
  },
  {
    id: 'conn-uw-loads-netsuite',
    name: 'uw-loads-netsuite',
    displayName: 'NetSuite — User Workflow Import Loads',
    description: 'NetSuite ERP connector purposed for User Workflow load import',
    version: '2024.1',
    isActive: true,
    applicationId: 'app-user-workflow',
    capabilityId: 'cap-uw-import-loads',
    sampleJsonSchema: '{"loadId":"string","customer":"string","amount":"number"}',
    createdAt: daysAgo(60),
    updatedAt: daysAgo(20),
    createdBy: 'devs',
  },
  {
    id: 'conn-ltl-orders-mcleod',
    name: 'ltl-orders-mcleod',
    displayName: 'McLeod v23 — LTL Navigator Import Orders',
    description: 'McLeod LoadMaster v23 connector purposed for LTL Navigator order import',
    version: '23',
    isActive: true,
    applicationId: 'app-ltl-navigator',
    capabilityId: 'cap-ltl-import-orders',
    sampleJsonSchema: '{"orderId":"string","origin":"string","destination":"string"}',
    createdAt: daysAgo(80),
    updatedAt: daysAgo(6),
    createdBy: 'devs',
  },
];

export const mockPartners: Partner[] = [
  {
    id: 'partner-001',
    name: 'Acme Logistics',
    description: 'Primary freight broker partner',
    createdAt: daysAgo(150),
    updatedAt: daysAgo(2),
    isDeleted: false,
    revision: 3,
  },
  {
    id: 'partner-002',
    name: 'Globex Transport',
    description: 'West coast carrier network',
    createdAt: daysAgo(100),
    updatedAt: daysAgo(8),
    isDeleted: false,
    revision: 1,
  },
  {
    id: 'partner-003',
    name: 'Initech Freight',
    description: 'Regional LTL provider',
    createdAt: daysAgo(75),
    updatedAt: daysAgo(15),
    isDeleted: false,
    revision: 2,
  },
  {
    id: 'partner-004',
    name: 'Umbrella Shipping',
    description: 'International ocean freight',
    createdAt: daysAgo(40),
    updatedAt: daysAgo(1),
    isDeleted: false,
    revision: 1,
  },
];

/**
 * 25 mock customers powering both the Integration Library "Customers" access tab
 * and the Customer Setup app. The Customer Setup app filters by
 * `integrationEnabled === true` via CustomerAccessService, so this array is the
 * single source of truth — toggling in the Library hides rows in Customer Setup.
 */
export const mockCustomers: Customer[] = [
  // ---- Initially Enabled (10) — these appear in Customer Setup by default ----
  {
    customerId: 'cust-001',
    customerName: 'Acme Logistics',
    expressCustomerCode: 'ACME',
    tmsName: 'mcleod-v23',
    lastSyncTime: isoDaysAgo(0),
    enabled: true,
    outboundEnabled: true,
    syncFrequencyMinutes: 15,
    orderRetentionDays: 90,
    syncBatchSize: 100,
    credentials: { apiKey: '***-***-***', username: 'acme_svc' },
    settings: { region: 'US', timezone: 'America/New_York' },
    updateOrInsertStatusesList: ['Available', 'Dispatched'],
    updateOnlyStatusesList: ['Delivered', 'Cancelled'],
    integrationEnabled: true,
    integrationStatusChangedAt: isoDaysAgo(2),
    applications: ['Mobile', 'Docs'],
  },
  {
    customerId: 'cust-002',
    customerName: 'Globex Transport',
    expressCustomerCode: 'GLBX',
    tmsName: 'sap-s4',
    lastSyncTime: isoDaysAgo(0),
    enabled: true,
    outboundEnabled: false,
    syncFrequencyMinutes: 30,
    orderRetentionDays: 60,
    credentials: { apiKey: '***-***-***' },
    updateOrInsertStatusesList: ['New', 'InTransit'],
    updateOnlyStatusesList: ['Completed'],
    integrationEnabled: true,
    integrationStatusChangedAt: isoDaysAgo(5),
    applications: ['Mobile'],
  },
  {
    customerId: 'cust-003',
    customerName: 'Initech Freight',
    expressCustomerCode: 'INTC',
    tmsName: 'webhook',
    lastSyncTime: isoDaysAgo(2),
    enabled: false,
    outboundEnabled: false,
    credentials: {},
    integrationEnabled: true,
    integrationStatusChangedAt: isoDaysAgo(9),
    applications: ['Docs'],
  },
  {
    customerId: 'cust-004',
    customerName: 'Stark Industries Shipping',
    expressCustomerCode: 'STRK',
    tmsName: 'mcleod-v23',
    lastSyncTime: isoDaysAgo(1),
    enabled: true,
    outboundEnabled: true,
    credentials: { apiKey: '***-***-***' },
    integrationEnabled: true,
    integrationStatusChangedAt: isoDaysAgo(14),
    applications: ['Mobile', 'Docs', 'Portal'],
  },
  {
    customerId: 'cust-005',
    customerName: 'Wayne Enterprises Logistics',
    expressCustomerCode: 'WAYN',
    tmsName: 'sap-s4',
    lastSyncTime: isoDaysAgo(3),
    enabled: true,
    outboundEnabled: true,
    credentials: { apiKey: '***-***-***' },
    integrationEnabled: true,
    integrationStatusChangedAt: isoDaysAgo(18),
    applications: ['Portal'],
  },
  {
    customerId: 'cust-006',
    customerName: 'Umbrella Distribution',
    expressCustomerCode: 'UMBR',
    tmsName: 'mcleod-v23',
    lastSyncTime: isoDaysAgo(0),
    enabled: true,
    outboundEnabled: false,
    credentials: { apiKey: '***-***-***' },
    integrationEnabled: true,
    integrationStatusChangedAt: isoDaysAgo(22),
    applications: ['Mobile', 'Docs'],
  },
  {
    customerId: 'cust-007',
    customerName: 'Tyrell Cargo Co.',
    expressCustomerCode: 'TYRL',
    tmsName: 'webhook',
    lastSyncTime: isoDaysAgo(4),
    enabled: true,
    outboundEnabled: true,
    credentials: { apiKey: '***-***-***' },
    integrationEnabled: true,
    integrationStatusChangedAt: isoDaysAgo(27),
    applications: ['Mobile'],
  },
  {
    customerId: 'cust-008',
    customerName: 'Soylent Freight',
    expressCustomerCode: 'SOYL',
    tmsName: 'sap-s4',
    lastSyncTime: isoDaysAgo(2),
    enabled: true,
    outboundEnabled: false,
    credentials: { apiKey: '***-***-***' },
    integrationEnabled: true,
    integrationStatusChangedAt: isoDaysAgo(33),
    applications: ['Docs', 'Portal'],
  },
  {
    customerId: 'cust-009',
    customerName: 'Cyberdyne Trucking',
    expressCustomerCode: 'CYBR',
    tmsName: 'mcleod-v23',
    lastSyncTime: isoDaysAgo(1),
    enabled: true,
    outboundEnabled: true,
    credentials: { apiKey: '***-***-***' },
    integrationEnabled: true,
    integrationStatusChangedAt: isoDaysAgo(41),
    applications: ['Mobile', 'Portal'],
  },
  {
    customerId: 'cust-010',
    customerName: 'Massive Dynamic Carriers',
    expressCustomerCode: 'MSDN',
    tmsName: 'webhook',
    lastSyncTime: isoDaysAgo(6),
    enabled: true,
    outboundEnabled: false,
    credentials: { apiKey: '***-***-***' },
    integrationEnabled: true,
    integrationStatusChangedAt: isoDaysAgo(48),
    applications: ['Docs'],
  },
  // ---- Initially Disabled (15) — legacy/long-tail; hidden from Customer Setup ----
  {
    customerId: 'cust-011',
    customerName: 'Pied Piper Shipping',
    expressCustomerCode: 'PIED',
    tmsName: 'webhook',
    lastSyncTime: isoDaysAgo(120),
    enabled: false,
    outboundEnabled: false,
    credentials: {},
    integrationEnabled: false,
    integrationStatusChangedAt: isoDaysAgo(55),
    applications: ['Docs'],
  },
  {
    customerId: 'cust-012',
    customerName: 'Hooli Logistics',
    expressCustomerCode: 'HOOL',
    tmsName: 'sap-s4',
    lastSyncTime: isoDaysAgo(180),
    enabled: false,
    outboundEnabled: false,
    credentials: {},
    integrationEnabled: false,
    integrationStatusChangedAt: isoDaysAgo(62),
    applications: ['Mobile'],
  },
  {
    customerId: 'cust-013',
    customerName: 'Vehement Capital Carriers',
    expressCustomerCode: 'VEHC',
    tmsName: 'mcleod-v23',
    lastSyncTime: isoDaysAgo(200),
    enabled: false,
    outboundEnabled: false,
    credentials: {},
    integrationEnabled: false,
    integrationStatusChangedAt: isoDaysAgo(68),
    applications: [],
  },
  {
    customerId: 'cust-014',
    customerName: 'Dunder Mifflin Freight',
    expressCustomerCode: 'DNDR',
    tmsName: 'webhook',
    lastSyncTime: isoDaysAgo(95),
    enabled: false,
    outboundEnabled: false,
    credentials: {},
    integrationEnabled: false,
    integrationStatusChangedAt: isoDaysAgo(73),
    applications: ['Docs'],
  },
  {
    customerId: 'cust-015',
    customerName: 'Sterling Cooper Transport',
    expressCustomerCode: 'STCO',
    tmsName: 'sap-s4',
    lastSyncTime: isoDaysAgo(150),
    enabled: false,
    outboundEnabled: false,
    credentials: {},
    integrationEnabled: false,
    integrationStatusChangedAt: isoDaysAgo(79),
    applications: ['Portal'],
  },
  {
    customerId: 'cust-016',
    customerName: 'Pendant Publishing Shipping',
    expressCustomerCode: 'PEND',
    tmsName: 'mcleod-v23',
    lastSyncTime: isoDaysAgo(160),
    enabled: false,
    outboundEnabled: false,
    credentials: {},
    integrationEnabled: false,
    integrationStatusChangedAt: isoDaysAgo(84),
    applications: [],
  },
  {
    customerId: 'cust-017',
    customerName: 'Vandelay Industries',
    expressCustomerCode: 'VNDL',
    tmsName: 'webhook',
    lastSyncTime: isoDaysAgo(220),
    enabled: false,
    outboundEnabled: false,
    credentials: {},
    integrationEnabled: false,
    integrationStatusChangedAt: isoDaysAgo(89),
    applications: ['Mobile'],
  },
  {
    customerId: 'cust-018',
    customerName: 'Kramerica Industries',
    expressCustomerCode: 'KRAM',
    tmsName: 'sap-s4',
    lastSyncTime: isoDaysAgo(240),
    enabled: false,
    outboundEnabled: false,
    credentials: {},
    integrationEnabled: false,
    integrationStatusChangedAt: isoDaysAgo(94),
    applications: ['Docs', 'Portal'],
  },
  {
    customerId: 'cust-019',
    customerName: 'Bluth Company Freight',
    expressCustomerCode: 'BLTH',
    tmsName: 'webhook',
    lastSyncTime: isoDaysAgo(300),
    enabled: false,
    outboundEnabled: false,
    credentials: {},
    integrationEnabled: false,
    integrationStatusChangedAt: isoDaysAgo(100),
    applications: [],
  },
  {
    customerId: 'cust-020',
    customerName: 'Los Pollos Hermanos Distribution',
    expressCustomerCode: 'LPHD',
    tmsName: 'mcleod-v23',
    lastSyncTime: isoDaysAgo(85),
    enabled: false,
    outboundEnabled: false,
    credentials: {},
    integrationEnabled: false,
    integrationStatusChangedAt: isoDaysAgo(105),
    applications: ['Mobile'],
  },
  {
    customerId: 'cust-021',
    customerName: 'Gringotts Cargo',
    expressCustomerCode: 'GRNG',
    tmsName: 'sap-s4',
    lastSyncTime: isoDaysAgo(310),
    enabled: false,
    outboundEnabled: false,
    credentials: {},
    integrationEnabled: false,
    integrationStatusChangedAt: isoDaysAgo(110),
    applications: ['Portal'],
  },
  {
    customerId: 'cust-022',
    customerName: 'Ollivanders Logistics',
    expressCustomerCode: 'OLLV',
    tmsName: 'webhook',
    lastSyncTime: isoDaysAgo(330),
    enabled: false,
    outboundEnabled: false,
    credentials: {},
    integrationEnabled: false,
    integrationStatusChangedAt: isoDaysAgo(118),
    applications: ['Docs'],
  },
  {
    customerId: 'cust-023',
    customerName: 'Tyrell Subsidiary Carriers',
    expressCustomerCode: 'TYSB',
    tmsName: 'mcleod-v23',
    lastSyncTime: isoDaysAgo(195),
    enabled: false,
    outboundEnabled: false,
    credentials: {},
    integrationEnabled: false,
    integrationStatusChangedAt: isoDaysAgo(126),
    applications: ['Mobile', 'Docs'],
  },
  {
    customerId: 'cust-024',
    customerName: 'Aperture Science Freight',
    expressCustomerCode: 'APSC',
    tmsName: 'sap-s4',
    lastSyncTime: isoDaysAgo(260),
    enabled: false,
    outboundEnabled: false,
    credentials: {},
    integrationEnabled: false,
    integrationStatusChangedAt: isoDaysAgo(135),
    applications: [],
  },
  {
    customerId: 'cust-025',
    customerName: 'Black Mesa Transport',
    expressCustomerCode: 'BLKM',
    tmsName: 'webhook',
    lastSyncTime: isoDaysAgo(280),
    enabled: false,
    outboundEnabled: false,
    credentials: {},
    integrationEnabled: false,
    integrationStatusChangedAt: isoDaysAgo(145),
    applications: ['Portal'],
  },
  // Truck Mate Logistics — disabled by default so the walkthrough demo starts
  // at Integration Library → Customers → Enable.
  {
    customerId: 'cust-truckmate',
    customerName: 'Truck Mate Logistics',
    expressCustomerCode: 'TM-001',
    tmsName: 'mcleod-v22',
    lastSyncTime: isoDaysAgo(0),
    enabled: false,
    outboundEnabled: false,
    credentials: {},
    integrationEnabled: false,
    integrationStatusChangedAt: isoDaysAgo(0),
    applications: [],
  },
];

export const mockApiClients: ApiClient[] = [
  {
    id: 'client-001',
    name: 'Acme Production',
    description: 'Production API client for Acme',
    isActive: true,
    createdAt: isoDaysAgo(120),
    updatedAt: isoDaysAgo(3),
    createdBy: 'admin',
  },
  {
    id: 'client-002',
    name: 'Globex Sandbox',
    description: 'Sandbox client for Globex testing',
    isActive: true,
    createdAt: isoDaysAgo(60),
    updatedAt: isoDaysAgo(7),
    createdBy: 'admin',
  },
  {
    id: 'client-003',
    name: 'Internal QA',
    description: 'Internal QA testing',
    isActive: false,
    createdAt: isoDaysAgo(180),
    updatedAt: isoDaysAgo(45),
  },
];

export const mockTemplates: FieldMappingTemplate[] = [
  {
    id: 'tmpl-001',
    name: 'McLeod → EDI 204',
    description: 'Map McLeod load to EDI 204 motor carrier load tender',
    version: 3,
    status: 'Published',
    latestVersionStatus: 'Published',
    createdAt: daysAgo(80),
    updatedAt: daysAgo(2),
    createdBy: 'admin',
    sourcePartnerId: 'partner-001',
    sourcePartnerName: 'Acme Logistics',
    targetPartnerId: 'partner-002',
    targetPartnerName: 'Globex Transport',
  },
  {
    id: 'tmpl-002',
    name: 'TMW → Acme Order JSON',
    description: 'Transform TMW dispatch records to Acme order format',
    version: 1,
    status: 'Draft',
    latestVersionStatus: 'Draft',
    createdAt: daysAgo(30),
    updatedAt: daysAgo(1),
    createdBy: 'admin',
    sourcePartnerId: 'partner-003',
    sourcePartnerName: 'Initech Freight',
    targetPartnerId: 'partner-001',
    targetPartnerName: 'Acme Logistics',
  },
  {
    id: 'tmpl-003',
    name: 'Prophesy → Status Update',
    description: 'Status webhooks from Prophesy into normalized event stream',
    version: 2,
    status: 'Published',
    latestVersionStatus: 'Draft',
    createdAt: daysAgo(50),
    updatedAt: daysAgo(10),
    createdBy: 'admin',
  },
];

export const mockTemplateVersions: { [templateId: string]: TemplateVersionResponse[] } = {
  'tmpl-001': [
    {
      id: 'tv-001-1',
      templateId: 'tmpl-001',
      templateName: 'McLeod → EDI 204',
      version: 1,
      status: 'Archived',
      publishedAt: isoDaysAgo(70),
    },
    {
      id: 'tv-001-2',
      templateId: 'tmpl-001',
      templateName: 'McLeod → EDI 204',
      version: 2,
      status: 'Archived',
      publishedAt: isoDaysAgo(40),
    },
    {
      id: 'tv-001-3',
      templateId: 'tmpl-001',
      templateName: 'McLeod → EDI 204',
      version: 3,
      status: 'Published',
      publishedAt: isoDaysAgo(2),
    },
  ],
  'tmpl-002': [
    { id: 'tv-002-1', templateId: 'tmpl-002', templateName: 'TMW → Acme Order JSON', version: 1, status: 'Draft' },
  ],
  'tmpl-003': [
    {
      id: 'tv-003-1',
      templateId: 'tmpl-003',
      templateName: 'Prophesy → Status Update',
      version: 1,
      status: 'Archived',
      publishedAt: isoDaysAgo(45),
    },
    {
      id: 'tv-003-2',
      templateId: 'tmpl-003',
      templateName: 'Prophesy → Status Update',
      version: 2,
      status: 'Published',
      publishedAt: isoDaysAgo(10),
    },
  ],
};

export const mockFieldMappings: FieldMapping[] = [
  {
    id: 'fm-001',
    templateId: 'tmpl-001',
    sourcePath: '$.order.id',
    targetPath: 'B2.04',
    transformationType: 'Direct',
    isRequired: true,
    createdAt: daysAgo(80),
    updatedAt: daysAgo(2),
  },
  {
    id: 'fm-002',
    templateId: 'tmpl-001',
    sourcePath: '$.order.pickup.city',
    targetPath: 'S5.01',
    transformationType: 'Direct',
    isRequired: true,
    createdAt: daysAgo(80),
    updatedAt: daysAgo(2),
  },
  {
    id: 'fm-003',
    templateId: 'tmpl-001',
    sourcePath: '$.order.weight',
    targetPath: 'L0.04',
    transformationType: 'Math',
    transformationConfig: '{"operation":"multiply","factor":1}',
    isRequired: false,
    defaultValue: '0',
    createdAt: daysAgo(80),
    updatedAt: daysAgo(2),
  },
  {
    id: 'fm-004',
    templateId: 'tmpl-002',
    sourcePath: '$.dispatch.loadNumber',
    targetPath: 'order.id',
    transformationType: 'Direct',
    isRequired: true,
    createdAt: daysAgo(30),
    updatedAt: daysAgo(1),
  },
  {
    id: 'fm-005',
    templateId: 'tmpl-002',
    sourcePath: '$.dispatch.status',
    targetPath: 'order.status',
    transformationType: 'Lookup',
    transformationConfig: '{"lookupTableId":"lkup-001"}',
    isRequired: true,
    defaultValue: 'UNKNOWN',
    createdAt: daysAgo(30),
    updatedAt: daysAgo(1),
  },
];

export const mockLookupTables: LookupTable[] = [
  {
    id: 'lkup-mcleod-status',
    tmsSystemId: 'conn-mobile-loads-mcleod-v23',
    fieldName: 'status',
    name: 'McLeod v23 Status Codes',
    description: 'Map McLeod load status codes to normalized values',
    mappings: JSON.stringify({ AVL: 'Available', DSP: 'Dispatched', INT: 'InTransit', DLV: 'Delivered' }),
    defaultValue: 'UNKNOWN',
    isCaseSensitive: false,
    createdAt: daysAgo(60),
    updatedAt: daysAgo(5),
  },
  {
    id: 'lkup-mcleod-equipment',
    tmsSystemId: 'conn-mobile-loads-mcleod-v23',
    fieldName: 'equipmentType',
    name: 'McLeod Equipment Codes',
    mappings: JSON.stringify({ V: 'Van', R: 'Reefer', F: 'Flatbed', T: 'Tanker' }),
    defaultValue: 'Other',
    isCaseSensitive: true,
    createdAt: daysAgo(40),
    updatedAt: daysAgo(3),
  },
  {
    id: 'lkup-sap-doctypes',
    tmsSystemId: 'conn-wfai-import-docs-sap',
    fieldName: 'docCategory',
    name: 'SAP Document Categories',
    description: 'Map SAP doc category codes to platform categories',
    mappings: JSON.stringify({ INV: 'Invoice', POD: 'ProofOfDelivery', RC: 'RateConfirmation' }),
    defaultValue: 'Other',
    isCaseSensitive: true,
    createdAt: daysAgo(30),
    updatedAt: daysAgo(7),
  },
  // ── Truck Mate (McLeod v22) cross-reference tables ─────────────────
  // Keyed by tmsName so the Mapping XREF dialog can resolve them for
  // any deployment whose customer.tmsName === 'mcleod-v22'.
  {
    id: 'lkup-mcleod-v22-status',
    tmsSystemId: 'mcleod-v22',
    fieldName: 'loadStatus',
    name: 'McLeod v22 Load Status Codes',
    description: 'Translates McLeod v22 dispatch status codes to platform status values.',
    mappings: JSON.stringify({
      AVL: 'Available',
      ASN: 'Assigned',
      DSP: 'Dispatched',
      ENR: 'EnRoute',
      ARR: 'AtPickup',
      LDG: 'Loading',
      INT: 'InTransit',
      DLV: 'Delivered',
      CNL: 'Cancelled',
    }),
    defaultValue: 'Unknown',
    isCaseSensitive: false,
    createdAt: daysAgo(45),
    updatedAt: daysAgo(2),
  },
  {
    id: 'lkup-mcleod-v22-equipment',
    tmsSystemId: 'mcleod-v22',
    fieldName: 'equipmentType',
    name: 'McLeod v22 Equipment Type Codes',
    description: 'Single-letter equipment codes used by McLeod v22.',
    mappings: JSON.stringify({
      V: 'Van',
      R: 'Reefer',
      F: 'Flatbed',
      T: 'Tanker',
      C: 'Container',
      D: 'Drayage',
      P: 'PowerOnly',
    }),
    defaultValue: 'Other',
    isCaseSensitive: true,
    createdAt: daysAgo(45),
    updatedAt: daysAgo(4),
  },
  {
    id: 'lkup-mcleod-v22-stoptypes',
    tmsSystemId: 'mcleod-v22',
    fieldName: 'stopType',
    name: 'McLeod v22 Stop Type Codes',
    description: 'Pickup / delivery / intermediate stop categorization.',
    mappings: JSON.stringify({
      PU: 'Pickup',
      DL: 'Delivery',
      DP: 'Dropoff',
      RL: 'Relay',
      FUEL: 'FuelStop',
      RST: 'Rest',
    }),
    defaultValue: 'Other',
    isCaseSensitive: false,
    createdAt: daysAgo(20),
    updatedAt: daysAgo(1),
  },
];

export const mockTransformationLogs: TransformationLogSummary[] = [
  {
    id: 'log-001',
    templateId: 'tmpl-001',
    templateName: 'McLeod → EDI 204',
    timestamp: isoDaysAgo(0),
    status: 'Success',
    durationMs: 142,
    recordCount: 1,
    source: 'API',
    hasErrors: false,
    hasOutput: true,
    correlationId: 'corr-aaa-001',
  },
  {
    id: 'log-002',
    templateId: 'tmpl-001',
    templateName: 'McLeod → EDI 204',
    timestamp: isoDaysAgo(0),
    status: 'Warning',
    durationMs: 198,
    recordCount: 1,
    messageSummary: 'Optional field missing',
    source: 'API',
    hasErrors: false,
    hasOutput: true,
    correlationId: 'corr-aaa-002',
  },
  {
    id: 'log-003',
    templateId: 'tmpl-002',
    templateName: 'TMW → Acme Order JSON',
    timestamp: isoDaysAgo(1),
    status: 'Error',
    durationMs: 87,
    recordCount: 0,
    messageSummary: 'Source path $.dispatch.loadNumber not found',
    source: 'API',
    hasErrors: true,
    hasOutput: false,
    correlationId: 'corr-bbb-001',
  },
  {
    id: 'log-004',
    templateId: 'tmpl-003',
    templateName: 'Prophesy → Status Update',
    timestamp: isoDaysAgo(1),
    status: 'PartialSuccess',
    durationMs: 312,
    recordCount: 5,
    messageSummary: '4 of 5 records mapped',
    source: 'Webhook',
    hasErrors: true,
    hasOutput: true,
    correlationId: 'corr-ccc-001',
  },
  {
    id: 'log-005',
    templateId: 'tmpl-001',
    templateName: 'McLeod → EDI 204',
    timestamp: isoDaysAgo(2),
    status: 'Success',
    durationMs: 121,
    recordCount: 1,
    source: 'API',
    hasErrors: false,
    hasOutput: true,
  },
  {
    id: 'log-006',
    templateId: 'tmpl-001',
    templateName: 'McLeod → EDI 204',
    timestamp: isoDaysAgo(3),
    status: 'Success',
    durationMs: 156,
    recordCount: 1,
    source: 'API',
    hasErrors: false,
    hasOutput: true,
  },
];

export const mockTransformationLogDetails: { [id: string]: TransformationLogDetail } = {
  'log-001': {
    ...mockTransformationLogs[0],
    inputData: JSON.stringify({ order: { id: 'A1001', pickup: { city: 'Tampa' }, weight: 42000 } }, null, 2),
    outputData: '{"B2.04":"A1001","S5.01":"Tampa","L0.04":42000}',
  },
  'log-003': {
    ...mockTransformationLogs[2],
    inputData: JSON.stringify({ dispatch: { status: 'AVL' } }, null, 2),
    errors: 'Source path $.dispatch.loadNumber not found in source document',
  },
};

export const mockApiClientTemplates: { [clientId: string]: TemplateVersionResponse[] } = {
  'client-001': [
    {
      id: 'tv-001-3',
      templateId: 'tmpl-001',
      templateName: 'McLeod → EDI 204',
      version: 3,
      status: 'Published',
      publishedAt: isoDaysAgo(2),
    },
    {
      id: 'tv-003-2',
      templateId: 'tmpl-003',
      templateName: 'Prophesy → Status Update',
      version: 2,
      status: 'Published',
      publishedAt: isoDaysAgo(10),
    },
  ],
  'client-002': [
    {
      id: 'tv-001-3',
      templateId: 'tmpl-001',
      templateName: 'McLeod → EDI 204',
      version: 3,
      status: 'Published',
      publishedAt: isoDaysAgo(2),
    },
  ],
  'client-003': [],
};

/**
 * Deployments — the (Customer × App × Capability × Connection × Template × Status)
 * bundle. Seeded from the ABC Carrier example in the architecture FigJam so the
 * Customer Detail screen has realistic data.
 */
export const mockDeployments: Deployment[] = [
  // ── Acme Logistics (cust-001) ────────────────────────────────────────────
  // Mobile only · Import Loads · Active
  {
    id: 'depl-001',
    customerId: 'cust-001',
    applicationId: 'app-mobile',
    capabilityId: 'cap-mobile-import-loads',
    connectionId: 'conn-mobile-loads-mcleod-v23',
    forkedFromTemplateId: 'tmpl-001',
    forkedFromTemplateVersion: 3,
    status: 'Active',
    createdAt: isoDaysAgo(60),
    updatedAt: isoDaysAgo(2),
    lastTestCorrelationId: 'corr-aaa-001',
    snapshotVersion: 2,
  },

  // ── Globex Transport (cust-002) ──────────────────────────────────────────
  // WorkflowAI: Import Documents (Active), Export Documents (Draft)
  // Mobile: Import Loads (Active), Export Scans (Published)
  {
    id: 'depl-002',
    customerId: 'cust-002',
    applicationId: 'app-workflowai',
    capabilityId: 'cap-wfai-import-docs',
    connectionId: 'conn-wfai-import-docs-mcleod',
    forkedFromTemplateId: 'tmpl-001',
    forkedFromTemplateVersion: 3,
    status: 'Active',
    createdAt: isoDaysAgo(120),
    updatedAt: isoDaysAgo(4),
    lastTestCorrelationId: 'corr-bbb-001',
    snapshotVersion: 3,
  },
  {
    id: 'depl-003',
    customerId: 'cust-002',
    applicationId: 'app-workflowai',
    capabilityId: 'cap-wfai-export-docs',
    connectionId: '',
    forkedFromTemplateId: '',
    forkedFromTemplateVersion: null,
    status: 'Draft',
    createdAt: isoDaysAgo(7),
    updatedAt: isoDaysAgo(2),
    snapshotVersion: 0,
  },
  {
    id: 'depl-004',
    customerId: 'cust-002',
    applicationId: 'app-mobile',
    capabilityId: 'cap-mobile-import-loads',
    connectionId: 'conn-mobile-loads-mcleod-v22',
    forkedFromTemplateId: 'tmpl-001',
    forkedFromTemplateVersion: 3,
    status: 'Active',
    createdAt: isoDaysAgo(90),
    updatedAt: isoDaysAgo(5),
    lastTestCorrelationId: 'corr-bbb-002',
    snapshotVersion: 2,
  },
  {
    id: 'depl-005',
    customerId: 'cust-002',
    applicationId: 'app-mobile',
    capabilityId: 'cap-mobile-export-scans',
    connectionId: 'conn-mobile-scans-sftp',
    forkedFromTemplateId: 'tmpl-002',
    forkedFromTemplateVersion: 1,
    status: 'Published',
    createdAt: isoDaysAgo(15),
    updatedAt: isoDaysAgo(3),
    snapshotVersion: 1,
  },

  // ── Initech Freight (cust-003) ───────────────────────────────────────────
  // All three apps fully configured · every capability Active
  {
    id: 'depl-006',
    customerId: 'cust-003',
    applicationId: 'app-workflowai',
    capabilityId: 'cap-wfai-import-docs',
    connectionId: 'conn-wfai-import-docs-sap',
    forkedFromTemplateId: 'tmpl-001',
    forkedFromTemplateVersion: 3,
    status: 'Active',
    createdAt: isoDaysAgo(150),
    updatedAt: isoDaysAgo(12),
    lastTestCorrelationId: 'corr-ccc-001',
    snapshotVersion: 5,
  },
  {
    id: 'depl-007',
    customerId: 'cust-003',
    applicationId: 'app-workflowai',
    capabilityId: 'cap-wfai-export-docs',
    connectionId: 'conn-wfai-export-docs-sap',
    forkedFromTemplateId: 'tmpl-002',
    forkedFromTemplateVersion: 1,
    status: 'Active',
    createdAt: isoDaysAgo(140),
    updatedAt: isoDaysAgo(10),
    lastTestCorrelationId: 'corr-ccc-002',
    snapshotVersion: 3,
  },
  {
    id: 'depl-008',
    customerId: 'cust-003',
    applicationId: 'app-workflowai',
    capabilityId: 'cap-wfai-webhooks',
    connectionId: 'conn-wfai-webhooks-receiver',
    forkedFromTemplateId: 'tmpl-003',
    forkedFromTemplateVersion: 2,
    status: 'Active',
    createdAt: isoDaysAgo(120),
    updatedAt: isoDaysAgo(8),
    lastTestCorrelationId: 'corr-ccc-003',
    snapshotVersion: 2,
  },
  {
    id: 'depl-009',
    customerId: 'cust-003',
    applicationId: 'app-mobile',
    capabilityId: 'cap-mobile-import-loads',
    connectionId: 'conn-mobile-loads-mcleod-v23',
    forkedFromTemplateId: 'tmpl-001',
    forkedFromTemplateVersion: 3,
    status: 'Active',
    createdAt: isoDaysAgo(100),
    updatedAt: isoDaysAgo(6),
    lastTestCorrelationId: 'corr-ccc-004',
    snapshotVersion: 2,
  },
  {
    id: 'depl-010',
    customerId: 'cust-003',
    applicationId: 'app-mobile',
    capabilityId: 'cap-mobile-export-scans',
    connectionId: 'conn-mobile-scans-sftp',
    forkedFromTemplateId: 'tmpl-002',
    forkedFromTemplateVersion: 1,
    status: 'Active',
    createdAt: isoDaysAgo(95),
    updatedAt: isoDaysAgo(5),
    lastTestCorrelationId: 'corr-ccc-005',
    snapshotVersion: 1,
  },
  {
    id: 'depl-011',
    customerId: 'cust-003',
    applicationId: 'app-user-workflow',
    capabilityId: 'cap-uw-import-loads',
    connectionId: 'conn-uw-loads-netsuite',
    forkedFromTemplateId: 'tmpl-002',
    forkedFromTemplateVersion: 1,
    status: 'Active',
    createdAt: isoDaysAgo(80),
    updatedAt: isoDaysAgo(3),
    lastTestCorrelationId: 'corr-ccc-006',
    snapshotVersion: 1,
  },
];

/**
 * Versions — the stacked history of each deployment.
 * Per DESIGN-STATUS-VERSIONING.md, every deployment has one or more
 * versions; exactly one Activated at a time; Drafts on top, Archived
 * below. Seed data is chosen to show interesting states across the
 * three demo customers (multi-draft stack, history + rollback,
 * Published-but-not-yet-Activated, plain single-version).
 */
export const mockVersions: Record<string, Version[]> = {
  // Acme · Mobile · Import Loads — Activated + two pending drafts (demos multi-draft stacking)
  'depl-001': [
    {
      id: 'ver-001-3',
      deploymentId: 'depl-001',
      versionNumber: 3,
      state: 'Draft',
      createdAt: isoDaysAgo(1),
      createdBy: 'Jake Cummings',
      notes: 'Tweaked driver-ID normalization',
    },
    {
      id: 'ver-001-2',
      deploymentId: 'depl-001',
      versionNumber: 2,
      state: 'Draft',
      createdAt: isoDaysAgo(4),
      createdBy: 'AJ Chen',
      notes: 'Alternate approach to load-status mapping',
    },
    {
      id: 'ver-001-1',
      deploymentId: 'depl-001',
      versionNumber: 1,
      state: 'Activated',
      createdAt: isoDaysAgo(60),
      createdBy: 'Jake Cummings',
      publishedAt: isoDaysAgo(58),
      activatedAt: isoDaysAgo(58),
    },
  ],

  // Globex · WorkflowAI · Import Documents — Activated with prior Archived (demos history + rollback)
  'depl-002': [
    {
      id: 'ver-002-2',
      deploymentId: 'depl-002',
      versionNumber: 2,
      state: 'Activated',
      createdAt: isoDaysAgo(45),
      createdBy: 'Mohammed Rahman',
      publishedAt: isoDaysAgo(42),
      activatedAt: isoDaysAgo(40),
      notes: 'Added support for revised invoice schema',
    },
    {
      id: 'ver-002-1',
      deploymentId: 'depl-002',
      versionNumber: 1,
      state: 'Archived',
      createdAt: isoDaysAgo(120),
      createdBy: 'Jake Cummings',
      publishedAt: isoDaysAgo(118),
      activatedAt: isoDaysAgo(118),
      archivedAt: isoDaysAgo(40),
    },
  ],

  // Globex · WorkflowAI · Export Documents — single Draft (demos chevron-as-button starting state)
  'depl-003': [
    {
      id: 'ver-003-1',
      deploymentId: 'depl-003',
      versionNumber: 1,
      state: 'Draft',
      createdAt: isoDaysAgo(7),
      createdBy: 'Allison Park',
      notes: 'Initial export config for AR department',
    },
  ],

  // Globex · Mobile · Import Loads — single Activated
  'depl-004': [
    {
      id: 'ver-004-1',
      deploymentId: 'depl-004',
      versionNumber: 1,
      state: 'Activated',
      createdAt: isoDaysAgo(90),
      createdBy: 'AJ Chen',
      publishedAt: isoDaysAgo(88),
      activatedAt: isoDaysAgo(88),
    },
  ],

  // Globex · Mobile · Export Scans — single Published, not yet Activated (demos the in-between)
  'depl-005': [
    {
      id: 'ver-005-1',
      deploymentId: 'depl-005',
      versionNumber: 1,
      state: 'Published',
      createdAt: isoDaysAgo(15),
      createdBy: 'Jake Cummings',
      publishedAt: isoDaysAgo(3),
      notes: 'Ready for review with the dispatch team',
    },
  ],

  // Initech — six simple Activated deployments, one version each
  'depl-006': [
    {
      id: 'ver-006-1',
      deploymentId: 'depl-006',
      versionNumber: 1,
      state: 'Activated',
      createdAt: isoDaysAgo(150),
      createdBy: 'Mohammed Rahman',
      publishedAt: isoDaysAgo(148),
      activatedAt: isoDaysAgo(148),
    },
  ],
  'depl-007': [
    {
      id: 'ver-007-1',
      deploymentId: 'depl-007',
      versionNumber: 1,
      state: 'Activated',
      createdAt: isoDaysAgo(140),
      createdBy: 'Allison Park',
      publishedAt: isoDaysAgo(138),
      activatedAt: isoDaysAgo(138),
    },
  ],
  'depl-008': [
    {
      id: 'ver-008-1',
      deploymentId: 'depl-008',
      versionNumber: 1,
      state: 'Activated',
      createdAt: isoDaysAgo(120),
      createdBy: 'AJ Chen',
      publishedAt: isoDaysAgo(118),
      activatedAt: isoDaysAgo(118),
    },
  ],
  'depl-009': [
    {
      id: 'ver-009-1',
      deploymentId: 'depl-009',
      versionNumber: 1,
      state: 'Activated',
      createdAt: isoDaysAgo(100),
      createdBy: 'Jake Cummings',
      publishedAt: isoDaysAgo(98),
      activatedAt: isoDaysAgo(98),
    },
  ],
  'depl-010': [
    {
      id: 'ver-010-1',
      deploymentId: 'depl-010',
      versionNumber: 1,
      state: 'Activated',
      createdAt: isoDaysAgo(95),
      createdBy: 'Allison Park',
      publishedAt: isoDaysAgo(93),
      activatedAt: isoDaysAgo(93),
    },
  ],
  'depl-011': [
    {
      id: 'ver-011-1',
      deploymentId: 'depl-011',
      versionNumber: 1,
      state: 'Activated',
      createdAt: isoDaysAgo(80),
      createdBy: 'Mohammed Rahman',
      publishedAt: isoDaysAgo(78),
      activatedAt: isoDaysAgo(78),
    },
  ],
};
